import { AnyAction } from 'redux';
import update /*, { Spec }*/ from 'immutability-helper';
import {
    UPDATE_FORM_CONTROLLER_VALUE,
    ADD_FORM_CONTROLLERS_TO_REPEATABLE_CONTROLLER,
    UPDATE_FORM_CONTROLLERS_IN_REPEATABLE_CONTROLLER,
    VALIDATE_FORM_DATA,
    SET_FORM_ERRORS,
    // SET_CALCULATOR_VALUES,
    FORM_INITIATED,
    PREPARE_FORM_DATA_IF_VALID,
    RESET_FORM_DATA_VALUES,
    RESET_FORM_CONTROLS_STATE,
    UPDATE_CONTROL_STATE,
    SET_FORM_STEP,
    MERGE_FORM_STATE,
    CREATE_FORM_STATE_SNAPSHOT,
    VALIDATE_LENDER_AMOUNTS,
    UPDATE_PROGRESS,
} from '../constants/actionTypes';
import {
    FormStatus,
    IActionWithPayload,
    IUpdateFormValueActionPayload,
    IWithControlName,
    IUpdateFormControllersToRepeatableControllerPayload,
    IAppStateContainer,
    IWithFormName,
    IAppState,
    IAppStateControlProperty,
    IFormSettings,
    IFormControl,
} from '../types/IFormSettings';
import { mapControls, prepareStore, getProgressRatio } from '../helpers';
import { prepareFormDataForSubmit } from '../helpers/mappers';
import { checkConditionalControls, checkConditionalFieldSets } from '../helpers/controlCondition.helper';
import { validateController, getErrorByCode } from '../helpers/validation.helper';
import { IValidationError } from '../types/IValidationError';
import { IFormError } from '../types/IFormError';
import { findControl } from '../helpers/findControl.helper';
// import { ICalculatorValues } from '../types/ICalculatorProps';

const hasNextStep = (form: IAppState, step: number | undefined) =>
    step !== undefined && Boolean(form.initialSettings.controls[step + 1]);

export const formReducer = (state: IAppStateContainer = {}, action: AnyAction) => {
    switch (action.type) {
        case FORM_INITIATED: {
            // update
            const { payload } = action as IActionWithPayload<IFormSettings>;
            const form = state[payload.formMeta.formName] || prepareStore(payload);
            form.formStatus = FormStatus.inProgress;
            form.valuesToSubmit = undefined;

            return update(state, {
                [payload.formMeta.formName]: { $set: form },
            });
        }

        case UPDATE_FORM_CONTROLLER_VALUE: {
            const { payload } = action as IActionWithPayload<IUpdateFormValueActionPayload>;
            const currentState = state[payload.formName];
            const control = currentState.controlsState[payload.controlName];
            control.value = payload.value;

            validateController(control, currentState);

            const updater = {
                [payload.formName]: {
                    controlsState: {
                        [payload.controlName]: { $set: { ...control } },
                    },
                    fieldSetState: {}
                },
            };

            Object.keys(currentState.fieldSetState).forEach((fieldSet) => {
                updater[payload.formName].fieldSetState = {
                    ...updater[payload.formName].fieldSetState,
                    [fieldSet]: { $set: { ...currentState.fieldSetState[fieldSet] } }
                }
            });

            checkConditionalControls(currentState, updater[payload.formName].controlsState);
            checkConditionalFieldSets(currentState, updater[payload.formName].fieldSetState);

            // The input's 'parentProp.disabled' property could later be used for skipping validation of the input, if the parent is disabled (usually a FieldSet).
            // Which is in theory also more effective than otherwise fetching the state of the FieldSet on demand, when that check is needed.
            // So instead, by updating the input's parentProp here to reflect the conditional FieldSet's disabled states, we have the data already available when needed.
            // Which would be preferred anyway, consistent data is generally liked. But NOTE that inputs (a.k.a controls) also can be stored in different parts
            // of the state object with different amounts of properties and with different values depending on how and when they are used.
            currentState.conditionalFieldSets.forEach((fieldSetName) => {
                const fieldSetFromState = currentState.fieldSetState[fieldSetName];
                const fieldSetDisabled = fieldSetFromState.disabled;

                fieldSetFromState.controls.forEach((ctrl) => {
                    const inputFromState = currentState.controlsState[ctrl.controlName];
                    const parentsProps = inputFromState.parentProps as IFormControl;

                    updater[payload.formName].controlsState[ctrl.controlName] = {
                        $set: { ...inputFromState, parentProps: { ...parentsProps, disabled: fieldSetDisabled } }
                    };
                });
            });

            let newState = update(state, updater);
            const currentStep = currentState.step ?? 0;

            const progressRatio = getProgressRatio(currentState);

            newState = update(newState, {
                [payload.formName]: {
                    progressRatio: { [currentStep]: { $set: progressRatio } },
                },
            });

            return newState;
        }

        // case SET_CALCULATOR_VALUES: {
        //     const { payload } = action as IActionWithPayload<ICalculatorValues>;
        //     const form = state[payload.formName];

        //     const updater: Spec<IAppStateContainer, any> = {
        //         [payload.formName]: {
        //             controlsState: {},
        //         },
        //     };

        //     const { controlsState } = updater[payload.formName];

        //     Object.keys(payload as any).forEach((controlName) => {
        //         const control = form.controlsState[controlName]
        //             ? { ...form.controlsState[controlName] }
        //             : {
        //                 state: { controlName },
        //                 value: undefined,
        //                 parentProps: undefined,
        //             };

        //         control.value = (payload as any)[controlName];

        //         controlsState[controlName] = { $set: control };
        //     });

        //     checkConditionalControls(form, controlsState);

        //     return update(state, updater);
        // }

        case ADD_FORM_CONTROLLERS_TO_REPEATABLE_CONTROLLER: {
            const { payload } = action as IActionWithPayload<IWithControlName>;
            const form = state[payload.formName];
            const control = { ...form.controlsState[payload.controlName] };
            control.controlInstances = control.controlInstances || [];
            const newLength = control.controlInstances.length;

            control.controlInstances = control.controlInstances.concat([
                (control.controls ?? []).map((item) => {
                    const newController = { ...item };
                    newController.controlName = `${payload.controlName}.${newLength}.${item.controlName.split(".").pop()}`;

                    mapControls(form, newController, control, form.step);

                    return newController;
                }),
            ]);

            return update(state, {
                [payload.formName]: {
                    controlsState: {
                        [payload.controlName]: { $set: control },
                    },
                },
            });
        }

        case UPDATE_FORM_CONTROLLERS_IN_REPEATABLE_CONTROLLER: {
            const { payload } = action as IActionWithPayload<IUpdateFormControllersToRepeatableControllerPayload>;
            const form = state[payload.formName];
            const controlsState = form.controlsState;
            const control = { ...form.controlsState[payload.controlName] };
            control.controlInstances = payload.newControls;
            let newFormState = controlsState;

            newFormState = update(newFormState, {
                [payload.controlName]: { $set: control }
            });

            payload.removedControls?.forEach((ctrl) => {
                newFormState = update(newFormState, { $unset: [ctrl.controlName] });
            })

            return update(state, {
                [payload.formName]: {
                    controlsState: { $set: newFormState }
                }
            });
        }

        case VALIDATE_LENDER_AMOUNTS: {
            const { payload: { formName } } = action as IActionWithPayload<IWithFormName>;
            const form = state[formName];
            const { controlsState } = form;
            const errors: IFormError[] = [];

            Object.keys(controlsState).forEach((controlName) => {
                if (controlName.includes('.lenderAmount') && controlsState[controlName].value) {
                    if (form.step !== undefined) {
                        const currentStep = form.initialSettings.controls?.[form.step];
                        // discard controls ouf of the current step
                        if (
                            !currentStep ||
                            !findControl(controlName, (currentStep as unknown) as IFormSettings)
                        )
                            return;
                    }

                    validateController(controlsState[controlName], form);

                    const controlState = controlsState[controlName];
                    const eventualLabel = controlState.label ? `${controlState.label}: ` : '';

                    if (controlState.isValid === false) {
                        const message =
                            controlState.validations?.[0].message !== undefined
                                ? controlState.validations?.[0].message
                                : eventualLabel + controlState.validationMessage;

                        errors.push({
                            controlName,
                            message: `${message}`,
                        });
                    }
                }
            });

            return update(state, {
                [formName]: {
                    errors: { $set: errors },
                    formStatus: {
                        $set: errors?.length ? FormStatus.notValid : FormStatus.inProgress,
                    },
                },
            });
        }

        case VALIDATE_FORM_DATA: {
            const { payload: { formName } } = action as IActionWithPayload<IWithFormName>;
            const form = state[formName];
            const { controlsState } = form;
            const errors: IFormError[] = [];

            Object.keys(controlsState).forEach((controlName) => {
                if (form.step !== undefined) {
                    const currentStep = form.initialSettings.controls?.[form.step];

                    // Skip inputs NOT present in the current step
                    if (!currentStep || !findControl(controlName, (currentStep as unknown) as IFormSettings))
                        return;
                }

                validateController(controlsState[controlName], form);

                const controlState = controlsState[controlName];
                const eventualLabel = controlState.label ? `${controlState.label}: ` : '';

                if (controlState.isValid === false) {
                    const message =
                        controlState.validations?.[0].message !== undefined
                            ? controlState.validations?.[0].message
                            : eventualLabel + controlState.validationMessage;

                    errors.push({
                        controlName,
                        message: `${message}`,
                    });
                }
            });

            const progressRatio = getProgressRatio(state[formName]);

            return update(state, {
                [formName]: {
                    errors: { $set: errors },
                    formStatus: {
                        $set: errors?.length ? FormStatus.notValid : FormStatus.inProgress,
                    },
                    progressRatio: { [form.step]: { $set: progressRatio } },
                },
            });
        }

        case PREPARE_FORM_DATA_IF_VALID: {
            const { payload: { formName } } = action as IActionWithPayload<IWithFormName>;
            const form = state[formName];
            const currentStep = form.step;

            if (form.errors?.length) {
                return update(state, {
                    [formName]: {
                        formStatus: { $set: FormStatus.notValid },
                    },
                });
            }

            if (hasNextStep(form, currentStep)) {
                return update(state, {
                    [formName]: {
                        valuesToSubmit: { $set: undefined },
                        formStatus: { $set: FormStatus.inProgress },
                        step: { $set: currentStep! + 1 },
                    },
                });
            }

            const valuesToSubmit = prepareFormDataForSubmit(form);

            return update(state, {
                [formName]: {
                    valuesToSubmit: { $set: valuesToSubmit },
                    formStatus: { $set: FormStatus.readyToSubmit },
                },
            });
        }

        case SET_FORM_ERRORS: {
            const { payload } = action as IActionWithPayload<IValidationError & IWithFormName>;
            const form = state[payload.formName];
            const { controlsState } = form;

            const errors = Array.isArray(form.errors) ? [...form.errors] : [];

            payload.invalidFields.forEach((field) => {
                const control = controlsState[field.controlName];

                if (control) {
                    control.isValid = false;
                    const { validations, label } = control;
                    const validationMessage = getErrorByCode(
                        validations || [],
                        field.code,
                        form.formMeta.requiredMessage,
                    );

                    errors.push({
                        controlName: field.controlName,
                        message: `${label} - ${validationMessage}`,
                    });
                } else {
                    errors.push({
                        controlName: field.controlName,
                        message: `${field.controlName} - is missing`,
                    });
                }
            });

            return update(state, {
                [payload.formName]: {
                    errors: { $set: errors },
                    formStatus: {
                        $set: errors?.length ? FormStatus.notValid : FormStatus.inProgress,
                    },
                },
            });
        }

        case RESET_FORM_DATA_VALUES: {
            const { payload } = action as IActionWithPayload<IWithFormName>;
            const form = state[payload.formName];
            form.valuesToSubmit = undefined;

            return update(state, {
                [payload.formName]: {
                    valuesToSubmit: { $set: undefined },
                    formStatus: { $set: FormStatus.inProgress },
                },
            });
        }

        case RESET_FORM_CONTROLS_STATE: {
            const { payload } = action as IActionWithPayload<IFormSettings>;
            const form = prepareStore(payload);
            form.formStatus = FormStatus.inProgress;
            form.valuesToSubmit = undefined;

            return update(state, {
                [payload.formMeta.formName]: { $set: form },
            });
        }

        case UPDATE_CONTROL_STATE: {
            const {
                payload: { formName, controlName, ...rest },
            } = action as IActionWithPayload<Partial<IFormControl> & IWithControlName>;
            const form = state[formName];
            const control = form.controlsState[controlName];

            const updater = {
                [formName]: {
                    controlsState: {
                        [controlName]: { $set: { ...control, ...rest } },
                    },
                },
            };

            return update(state, updater);
        }

        case MERGE_FORM_STATE: {
            const { payload: { formName, controlsState, step } } = action as IActionWithPayload<IWithFormName & { controlsState: IAppStateControlProperty; step: number }>;
            let newFormState = state[formName].controlsState;

            Object.keys(controlsState).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(newFormState, key)) {
                    newFormState = update(newFormState, {
                        [key]: { value: { $set: controlsState[key].value } },
                    });
                } else {
                    newFormState = update(newFormState, { [key]: { $set: controlsState[key] } });
                }
            });

            const progressRatio = getProgressRatio(state[formName]);

            return update(state, {
                [formName]: {
                    controlsState: { $set: newFormState },
                    progressRatio: { 0: { $set: progressRatio } },
                    step: { $set: step > 0 ? step - 1 : 0 },
                },
            });
        }

        case SET_FORM_STEP: {
            const { payload: { formName, value } } = action as IActionWithPayload<IWithFormName & { value: number }>;
            return update(state, { [formName]: { step: { $set: value } } });
        }

        case CREATE_FORM_STATE_SNAPSHOT: {
            const { payload: { formName } } = action as IActionWithPayload<IWithFormName>;
            return update(state, {
                [formName]: { controlsStateSnapshot: { $set: state[formName].controlsState } },
            });
        }

        case UPDATE_PROGRESS: {
            const { payload: { formName } } = action as IActionWithPayload<IWithFormName>;
            const progressRatio = getProgressRatio(state[formName]);

            return update(state, {
                [formName]: {
                    progressRatio: { [state[formName].step]: { $set: progressRatio } },
                }
            });
        }

        default:
            return state;
    }
};
